import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { CardActionArea } from "@mui/material";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { AsolviSwitch } from "components/AsolviSwitch";
import FileCardContent from "components/FileCardContent";
import FileTabCard from "components/FileTabCard";
import { RenderFile } from "components/RenderFile";
import { downloadFile, formatBytes } from "helpers";
import { FileInputType, FileType } from "operations/schema/schema";
import { FC, useCallback, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useAppDispatch, useAppSelector } from "store";
import { openFilePreview } from "store/slices/dialogs/filePreview.store";
import { isFlagEnabled } from "store/slices/user.store";

export interface FilePreviewProps {
  file: FileInputType | FileType;
  onDelete?: () => void;
  onEdit?: () => void;
  downloadOnCardClick?: boolean;
  readonly?: boolean;
  disableEdit?: boolean;
  onChangeUseInServiceReport?: (value: boolean) => void;
  useInServiceReportHidden?: boolean;
  disableServiceReport?: boolean;
}

const PreviewExtensions = [
  "png",
  "jpg",
  "jpeg",
  "bmp",
  "gif",
  "tif",
  "tiff",
  "pdf",
  "mp4",
  "ogg",
  "webm",
];

export const FilePreview: FC<FilePreviewProps> = (props) => {
  const {
    file,
    onDelete,
    onEdit,
    downloadOnCardClick,
    readonly,
    disableEdit,
    onChangeUseInServiceReport,
    useInServiceReportHidden,
    disableServiceReport,
  } = props;
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const useInServiceReportEnabled =
    useAppSelector((s) => isFlagEnabled(s, "DocumentUseInServiceReport")) &&
    !useInServiceReportHidden;

  const handleDelete = useCallback(
    (event: any) => {
      if (!onDelete) return;
      event.preventDefault();
      onDelete();
    },
    [onDelete],
  );

  const isPreviewable = useMemo(() => {
    // eslint-disable-next-line
    const parts = file.name?.split(".");
    if (!parts || parts.length !== 2) return false;
    const ext = parts[1];
    return PreviewExtensions.includes(ext);
  }, [file.name]);

  const onDoubleClick = useCallback(() => {
    if (downloadOnCardClick) {
      downloadFile(file);
    }
  }, [downloadOnCardClick, file]);

  const onClick = useCallback(() => {
    if (isPreviewable) {
      dispatch(openFilePreview({ file }));
    } else if (downloadOnCardClick) {
      downloadFile(file);
    }
  }, [dispatch, downloadOnCardClick, file, isPreviewable]);

  return (
    <Grid item xs={12} width="100%">
      <FileTabCard key={file.name} data-testid={`FilePreview-FileTabCard-${file.name}`}>
        <CardActionArea
          onDoubleClick={onDoubleClick}
          onClick={onClick}
          sx={{
            cursor: isPreviewable || downloadOnCardClick ? "pointer" : "default",
            display: "flex",
            ".MuiCardActionArea-focusHighlight": {
              background: "transparent",
            },
          }}
          disableRipple
          disableTouchRipple
          style={{ width: "auto" }}
        >
          <RenderFile file={file} />
        </CardActionArea>
        <FileCardContent>
          <CardActionArea
            onDoubleClick={onDoubleClick}
            onClick={onClick}
            sx={{
              cursor: isPreviewable || downloadOnCardClick ? "pointer" : "default",
              display: "flex",
              flexDirection: "column",
              alignItems: "start",
              flexGrow: 1,
              ".MuiCardActionArea-focusHighlight": {
                background: "transparent",
              },
            }}
            disableRipple
            disableTouchRipple
          >
            <Typography
              component="p"
              variant="body2"
              sx={{
                wordWrap: "break-word",
              }}
            >
              {file.name}
            </Typography>
            <Typography variant="subtitle1" color="textSecondary">
              {file?.data?.length && formatBytes(file.data.length)}
            </Typography>
          </CardActionArea>
          {useInServiceReportEnabled && (
            <Grid container>
              <Grid item marginRight={1}>
                <Typography variant="body2">
                  <FormattedMessage id="file.useInServiceReport" />
                </Typography>
              </Grid>
              <Grid item>
                <AsolviSwitch
                  title={intl.formatMessage({ id: "file.useInServiceReport" })}
                  checked={file.useInServiceReport ?? false}
                  onChange={(_, checked) => {
                    if (onChangeUseInServiceReport) {
                      onChangeUseInServiceReport(checked);
                    }
                  }}
                  disabled={disableServiceReport}
                />
              </Grid>
            </Grid>
          )}
        </FileCardContent>
        {!readonly && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              flexDirection: "column",
            }}
          >
            {onDelete && (
              <IconButton
                aria-label={intl.formatMessage({ id: "general.delete" })}
                onClick={handleDelete}
                data-testid="FilePreview-DeleteButton"
                size="large"
              >
                <DeleteIcon color="error" />
              </IconButton>
            )}
            {file.data?.startsWith("data:image/") && onEdit && !disableEdit && (
              <IconButton
                aria-label={intl.formatMessage({ id: "general.edit" })}
                onClick={onEdit}
                data-testid="FilePreview-EditButton"
                size="large"
              >
                <EditIcon color="action" />
              </IconButton>
            )}
          </div>
        )}
      </FileTabCard>
    </Grid>
  );
};
